<template>
  <div>
    <div class="card mr-4">
      <div class="card-header d-flex justify-content-between align-items-center border-0">
        <span class="card-title mb-0">Commission Payment Settings</span>
        <button class="btn btn-sm btn-primary" @click="toggleModal('add')">
          <i class="fa fa-plus"></i> Add New
        </button>
      </div>
    </div>

    <div class="row commission-settings mr-2">
      <div v-if="isLoading" class="col-12 text-center">Loading data..</div>
      <div v-else class="col-12 col-md-6" v-for="setting in commissionSettings" :key="setting.id">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center bg-white">
            <div class="btn-action">
              <button class="btn btn-sm btn-primary" @click="toggleModal('edit', setting.id)">
                <i class="fa fa-edit"></i> Edit setting
              </button>
              <button class="btn btn-sm btn-secondary" @click="duplicateSetting(setting.id)" :disabled="isDuplicating">
                <i class="fa fa-copy"></i> {{ isDuplicating ? 'Duplicating..' : 'Duplicate' }}
              </button>
              <button v-if="!setting.is_default" class="btn btn-sm btn-info" @click="setDefaultSetting(setting.id)">
                <i class="fa fa-check-circle"></i> Set Default
              </button>
            </div>
            <span v-if="setting.is_default" class="card-title mb-0">
              Status: Active
            </span>
            <button v-else-if="setting.closed_commission_count <= 0" class="btn btn-sm btn-danger" @click="confirmDelete(setting.id)">
              <i class="fa fa-trash"></i> Delete
            </button>
          </div>
          <div class="card-body">
            <h4>Rules:</h4>
            <ul>
              <li v-for="(rule, index) in setting.rules" :key="index">
                From ${{ rule.min_payment }} to ${{ rule.max_payment }} => ${{ rule.commission }}
              </li>
            </ul>
            <h4>Enhancers:</h4>
            <ul>
              <li v-for="(enhancer, index) in setting.enhancers" :key="index">
                {{ getEnhancerType(enhancer.type) }} => {{ enhancer.percentage }}%
              </li>
            </ul>
            <h4 v-if="setting.level_enhancers.length > 0">Level Enhancers:</h4>
            <ul v-if="setting.level_enhancers.length > 0">
              <li v-for="(enhancer, index) in setting.level_enhancers" :key="index">
                {{ getEnhancerType(enhancer.name) }} => {{ enhancer.percentage }}%
              </li>
            </ul>
            <h4>Quantity Enhancers:</h4>
            <ul>
              <li v-for="(qtyEnhancer, index) in setting.qty_enhancers" :key="index">
                {{ qtyEnhancer.min_students }} to {{ qtyEnhancer.max_students }} students => {{ qtyEnhancer.percentage }}%
              </li>
            </ul>
            <h4 v-if="setting.school_types.length > 0">School Types:</h4>
            <ul v-if="setting.school_types.length > 0">
              <li v-for="(types, schoolType) in groupedSchoolTypes[setting.id]" :key="schoolType">
                {{ schoolType }}:
                <ul>
                  <li v-for="(type, index) in types" :key="index">
                    From ${{ type.min_payment }} to ${{ type.max_payment }} => ${{ type.amount }}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <add-commission-setting-modal ref="addModal" :user-id="userId" @added="fetchCommissionSettings" />
    <edit-commission-setting-modal ref="editModal" :user-id="userId" @updated="fetchCommissionSettings" />
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { paymentSettings } from "../../services/api/commissions";
import AddCommissionSettingModal from "./components/AddCommissionSettingModal.vue";
import EditCommissionSettingModal from "./components/EditCommissionSettingModal.vue";

export default {
  name: "PaymentSettings",
  components: {
    AddCommissionSettingModal,
    EditCommissionSettingModal
  },
  props: {
    userId: {
      type: Number,
      default: 0,
    }
  },
  data() {
    return {
      isLoading: false,
      isDuplicating: false,
      commissionSettings: []
    };
  },
  mounted() {
    this.fetchCommissionSettings();
  },
  computed: {
    groupedSchoolTypes() {
      return this.commissionSettings.reduce((grouped, setting) => {
        grouped[setting.id] = setting.school_types.reduce((types, type) => {
          const schoolType = this.getSchoolType(type.school_type);
          types[schoolType] = types[schoolType] || [];
          types[schoolType].push({
            min_payment: type.min_payment,
            max_payment: type.max_payment,
            amount: type.amount
          });
          return types;
        }, {});
        return grouped;
      }, {});
    }
  },
  methods: {
    getEnhancerType(type) {
      const typeMapping = {
        'key_partner': 'Key Partner',
        'reference': 'Reference',
        'self': 'Self Generated',
        'platinum': 'Platinum',
        'gold': 'Gold',
        'silver': 'Silver',
        'budget': 'Budget',
      };
      return typeMapping[type] || type;
    },
    getSchoolType(type) {
      const typeMapping = {
        'tafe': 'Tafe',
        'public': 'Public',
        'higher_education': 'Higher Education'
      };
      return typeMapping[type] || type;
    },
    async fetchCommissionSettings() {
      try {
        this.isLoading = true;
        const response = await paymentSettings.list(this.userId);

        if (response.status === 200) {
          this.commissionSettings = response.data;
        }
      } catch (error) {
        console.error('Error fetching commission settings:', error);

        new Noty({
          type: "error",
          text: 'Failed to fetch commission payment settings. Please try again later.',
        }).show();
      } finally {
        this.isLoading = false;
      }
    },
    toggleModal(mode, commissionId = null) {
      if (mode === 'add') {
        this.$refs.addModal.hasSettings = this.commissionSettings.length > 0;
        this.$refs.addModal.showModal = true;
      } else if (mode === 'edit') {
        this.$refs.editModal.commissionId = commissionId;
        this.$refs.editModal.openModal();
      }
    },
    confirmDelete(id) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this payment setting!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it',
        reverseButtons: true,
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger mx-1',
          cancelButton: 'btn btn-secondary mx-1'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteSetting(id);
        }
      });
    },
    async deleteSetting(id) {
      try {
        const response = await paymentSettings.delete(this.userId, id);

        if (response.status === 204) {
          await Swal.fire({
            title: 'Deleted',
            text: 'Your setting has been deleted.',
            icon: 'success',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });

          // Refreshing data after successful deletion
          await this.fetchCommissionSettings();
        }
      } catch (error) {
        await Swal.fire(
          'Error!',
          'Failed to delete setting. Please try again later.',
          'error'
        );
      }
    },
    async duplicateSetting(id) {
      try {
        this.isDuplicating = true;
        const settingToDuplicate = this.commissionSettings.find(setting => setting.id === id);

        if (!settingToDuplicate) {
          return;
        }

        const schoolTypes = settingToDuplicate.school_types?.length
          ? settingToDuplicate.school_types.map(schoolType => ({
            school_type: schoolType.school_type,
            paymentRules: [{
              min_payment: schoolType.min_payment,
              max_payment: schoolType.max_payment,
              amount: schoolType.amount
            }]
          }))
          : null;

        // Assign existing data to payload
        const duplicatedSetting = {
          ...settingToDuplicate,
          id: undefined,
          is_default: 0,
          enhancers: settingToDuplicate.enhancers,
          level_enhancers: settingToDuplicate.level_enhancers ?? null,
          school_types: schoolTypes
        };

        const response = await paymentSettings.create(this.userId, duplicatedSetting);

        if (response.status === 201) {
          await Swal.fire({
            title: 'Duplicated!',
            text: 'The commission setting has been duplicated successfully.',
            icon: 'success',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });

          await this.fetchCommissionSettings();
        }
      } catch (error) {
        console.error('Error duplicating setting:', error);
        await Swal.fire({
          title: 'Error!',
          text: 'Failed to duplicate setting. Please try again later.',
          icon: 'error',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
      } finally {
        this.isDuplicating = false;
      }
    },
    async setDefaultSetting(id) {
      try {
        console.log(this.userId)
        const settingToDefault = this.commissionSettings.find(setting => setting.id === id);

        if (!settingToDefault) {
          return;
        }

        const schoolTypes = settingToDefault.school_types?.length
            ? settingToDefault.school_types.map(schoolType => ({
              school_type: schoolType.school_type,
              paymentRules: [{
                min_payment: schoolType.min_payment,
                max_payment: schoolType.max_payment,
                amount: schoolType.amount
              }]
            }))
            : null;

        const payload = {
          ...settingToDefault,
          is_default: 1,
          enhancers: settingToDefault.enhancers,
          level_enhancers: settingToDefault.level_enhancers ?? null,
          school_types: schoolTypes
        };

        if (payload.school_types.every(type => !type.school_type)) {
          delete payload.school_types;
        }

        const response = await paymentSettings.update(this.userId, settingToDefault.id, payload);

        if (response.status === 200) {
          await Swal.fire({
            title: 'Set the default',
            text: 'The commission setting has been set as default.',
            icon: 'success',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });

          await this.fetchCommissionSettings();
        }

      } catch (error) {
        console.error('Error set default setting:', error);
        await Swal.fire({
          title: 'Error!',
          text: 'Failed to set default setting. Please try again later.',
          icon: 'error',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-primary'
          }
        });
      }
    }
  }
}
</script>